import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCart, addToCart, updateCartItem, removeFromCart, clearCart } from '../lib/store/cart';

export const cartKeys = {
    all: ['cart'] as const,
    detail: () => [...cartKeys.all, 'detail'] as const,
};

export const useCart = () => {
    return useQuery({
        queryKey: cartKeys.detail(),
        queryFn: async ({ signal }) => {
            try {
                const result = await getCart(signal);
                return result;
            } catch (error: any) {
                // Żądanie anulowane (np. superseded podczas wylogowania) - nie jest
                // to prawdziwy błąd, więc przepuszczamy je dalej. React Query samo
                // wie, jak obsłużyć anulowane zapytanie (nie ustawia stanu error).
                if (error?.code === 'ERR_CANCELED') {
                    throw error;
                }
                console.error('❌ useCart - błąd pobierania:', error);
                // Zwracamy pusty koszyk zamiast rzucać błąd
                return {
                    cart_hash: '',
                    cart_key: '',
                    items: [],
                    item_count: 0,
                    items_weight: '0',
                    totals: {
                        subtotal: '0',
                        subtotal_tax: '0',
                        fee_total: '0',
                        fee_tax: '0',
                        discount_total: '0',
                        discount_tax: '0',
                        shipping_total: '0',
                        shipping_tax: '0',
                        total: '0',
                        total_tax: '0',
                    },
                    coupons: [],
                    needs_payment: false,
                    needs_shipping: false,
                    currency: {
                        currency_code: 'PLN',
                        currency_symbol: 'zł',
                    },
                };
            }
        },
        staleTime: 0,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        retry: 1,
    });
};

export const useAddToCart = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: addToCart,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: cartKeys.detail() });
            // Małe opóźnienie dla pewności synchronizacji
            setTimeout(() => {
                queryClient.refetchQueries({ queryKey: cartKeys.detail() });
            }, 300);
        },
        onError: (error) => {
            console.error('❌ Błąd dodawania do koszyka:', error);
        },
    });
};

export const useUpdateCartItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateCartItem,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: cartKeys.detail() });
            setTimeout(() => {
                queryClient.refetchQueries({ queryKey: cartKeys.detail() });
            }, 300);
        },
        onError: (error) => {
            console.error('❌ Błąd aktualizacji koszyka:', error);
        },
    });
};

export const useRemoveFromCart = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: removeFromCart,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: cartKeys.detail() });
            setTimeout(() => {
                queryClient.refetchQueries({ queryKey: cartKeys.detail() });
            }, 300);
        },
        onError: (error) => {
            console.error('❌ Błąd usuwania z koszyka:', error);
        },
    });
};

export const useClearCart = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: clearCart,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: cartKeys.detail() });
            setTimeout(() => {
                queryClient.refetchQueries({ queryKey: cartKeys.detail() });
            }, 300);
        },
        onError: (error) => {
            console.error('❌ Błąd czyszczenia koszyka:', error);
        },
    });
};
